import type {
  KpiMetric,
  UtilizationDataPoint,
  BusinessPurposeWeekData,
  HeatmapCell,
  HeatmapTier,
  BusinessPurposeConfig,
  DashboardConfig,
  RoomDetail,
  RoomTypeConfig,
  ScheduleEvent,
  BusinessGroupConsumption,
  SurveySiteCSAT,
  SurveyTrendPoint,
  PurposeSatisfaction,
  SpaceTypeSatisfaction,
} from '@/types/reports';
import { tokens } from '@fluentui/react-components';

export const BUSINESS_PURPOSES: BusinessPurposeConfig[] = [
  { name: 'Academic', color: tokens.colorPaletteRoyalBlueForeground2 },
  { name: 'Community Affairs', color: tokens.colorPaletteCranberryForeground2 },
  { name: 'Executive', color: tokens.colorPaletteTealForeground2 },
  { name: 'Government', color: tokens.colorPalettePurpleForeground2 },
  { name: 'Human Resources', color: tokens.colorPaletteGreenForeground2 },
  { name: 'MS Customer', color: tokens.colorPaletteBlueForeground2 },
  { name: 'MS Internal', color: tokens.colorPaletteRedForeground2 },
  { name: 'MS Partner', color: tokens.colorPaletteForestForeground2 },
  { name: 'Non-MS Business', color: tokens.colorPalettePlumForeground2 },
  { name: 'Non-Profit', color: tokens.colorPaletteBrownForeground2 },
  { name: 'Training', color: tokens.colorPaletteGrapeForeground2 },
  { name: 'User Group', color: tokens.colorPaletteMagentaForeground2 },
  { name: 'Walk Ins', color: tokens.colorPaletteSeafoamForeground2 },
];

export const MOCK_KPI_METRICS: KpiMetric[] = [
  {
    id: 'avg-utilization',
    labelKey: 'kpi.avgUtilization',
    value: '74.2%',
    unit: '%',
    trendDirection: 'positive',
    trendValue: '+3.1%',
  },
  {
    id: 'total-booked-hours',
    labelKey: 'kpi.totalBookedHours',
    value: '18,432 h',
    unit: 'h',
    trendDirection: 'positive',
    trendValue: '+6.2%',
  },
  {
    id: 'available-hours',
    labelKey: 'kpi.availableHours',
    value: '24,840 h',
    unit: 'h',
    trendDirection: 'neutral',
  },
  {
    id: 'peak-day',
    labelKey: 'kpi.peakDay',
    value: 'Wednesday',
    trendDirection: 'positive',
    trendLabel: '91% peak',
  },
];

export const MOCK_UTILIZATION_TREND: UtilizationDataPoint[] = [
  { period: 'W1 Jan', utilization: 68 },
  { period: 'W2 Jan', utilization: 71 },
  { period: 'W3 Jan', utilization: 73 },
  { period: 'W4 Jan', utilization: 75 },
  { period: 'W1 Feb', utilization: 76 },
  { period: 'W2 Feb', utilization: 74 },
  { period: 'W3 Feb', utilization: 77 },
  { period: 'W4 Feb', utilization: 78 },
  { period: 'W1 Mar', utilization: 79 },
  { period: 'W2 Mar', utilization: 80 },
  { period: 'W3 Mar', utilization: 79 },
  { period: 'W4 Mar', utilization: 81 },
  { period: 'W1 Apr', utilization: 80 },
  { period: 'W2 Apr', utilization: 82 },
  { period: 'W3 Apr', utilization: 81 },
  { period: 'W4 Apr', utilization: 83 },
  { period: 'W1 May', utilization: 82 },
  { period: 'W2 May', utilization: 84 },
  { period: 'W3 May', utilization: 83 },
  { period: 'W4 May', utilization: 85 },
  { period: 'W1 Jun', utilization: 84 },
  { period: 'W2 Jun', utilization: 83 },
  { period: 'W3 Jun', utilization: 85 },
  { period: 'W4 Jun', utilization: 84 },
];

export const MOCK_BUSINESS_PURPOSE_DATA: BusinessPurposeWeekData[] = [
  {
    period: 'W1 Jan',
    Academic: 8,
    'Community Affairs': 5,
    Executive: 12,
    Government: 4,
    'Human Resources': 6,
    'MS Customer': 10,
    'MS Internal': 14,
    'MS Partner': 3,
    'Non-MS Business': 4,
    'Non-Profit': 2,
    Training: 5,
    'User Group': 3,
    'Walk Ins': 6,
  },
  {
    period: 'W2 Jan',
    Academic: 9,
    'Community Affairs': 6,
    Executive: 11,
    Government: 5,
    'Human Resources': 7,
    'MS Customer': 11,
    'MS Internal': 13,
    'MS Partner': 4,
    'Non-MS Business': 5,
    'Non-Profit': 2,
    Training: 4,
    'User Group': 4,
    'Walk Ins': 5,
  },
  {
    period: 'W3 Jan',
    Academic: 7,
    'Community Affairs': 5,
    Executive: 13,
    Government: 4,
    'Human Resources': 6,
    'MS Customer': 12,
    'MS Internal': 15,
    'MS Partner': 3,
    'Non-MS Business': 4,
    'Non-Profit': 3,
    Training: 6,
    'User Group': 3,
    'Walk Ins': 7,
  },
  {
    period: 'W4 Jan',
    Academic: 8,
    'Community Affairs': 6,
    Executive: 12,
    Government: 5,
    'Human Resources': 7,
    'MS Customer': 11,
    'MS Internal': 14,
    'MS Partner': 4,
    'Non-MS Business': 5,
    'Non-Profit': 2,
    Training: 5,
    'User Group': 4,
    'Walk Ins': 6,
  },
  {
    period: 'W1 Feb',
    Academic: 9,
    'Community Affairs': 5,
    Executive: 14,
    Government: 4,
    'Human Resources': 6,
    'MS Customer': 12,
    'MS Internal': 15,
    'MS Partner': 3,
    'Non-MS Business': 4,
    'Non-Profit': 2,
    Training: 6,
    'User Group': 3,
    'Walk Ins': 7,
  },
  {
    period: 'W2 Feb',
    Academic: 8,
    'Community Affairs': 6,
    Executive: 13,
    Government: 5,
    'Human Resources': 7,
    'MS Customer': 11,
    'MS Internal': 14,
    'MS Partner': 4,
    'Non-MS Business': 5,
    'Non-Profit': 3,
    Training: 5,
    'User Group': 4,
    'Walk Ins': 6,
  },
  {
    period: 'W3 Feb',
    Academic: 9,
    'Community Affairs': 5,
    Executive: 14,
    Government: 4,
    'Human Resources': 8,
    'MS Customer': 12,
    'MS Internal': 16,
    'MS Partner': 3,
    'Non-MS Business': 4,
    'Non-Profit': 2,
    Training: 6,
    'User Group': 3,
    'Walk Ins': 7,
  },
  {
    period: 'W4 Feb',
    Academic: 8,
    'Community Affairs': 6,
    Executive: 13,
    Government: 5,
    'Human Resources': 7,
    'MS Customer': 13,
    'MS Internal': 15,
    'MS Partner': 4,
    'Non-MS Business': 5,
    'Non-Profit': 3,
    Training: 5,
    'User Group': 4,
    'Walk Ins': 6,
  },
  {
    period: 'W1 Mar',
    Academic: 9,
    'Community Affairs': 6,
    Executive: 14,
    Government: 5,
    'Human Resources': 7,
    'MS Customer': 12,
    'MS Internal': 15,
    'MS Partner': 4,
    'Non-MS Business': 5,
    'Non-Profit': 2,
    Training: 6,
    'User Group': 4,
    'Walk Ins': 7,
  },
  {
    period: 'W2 Mar',
    Academic: 10,
    'Community Affairs': 6,
    Executive: 14,
    Government: 5,
    'Human Resources': 8,
    'MS Customer': 13,
    'MS Internal': 16,
    'MS Partner': 4,
    'Non-MS Business': 5,
    'Non-Profit': 3,
    Training: 6,
    'User Group': 4,
    'Walk Ins': 7,
  },
  {
    period: 'W3 Mar',
    Academic: 9,
    'Community Affairs': 7,
    Executive: 15,
    Government: 5,
    'Human Resources': 8,
    'MS Customer': 13,
    'MS Internal': 16,
    'MS Partner': 4,
    'Non-MS Business': 5,
    'Non-Profit': 3,
    Training: 7,
    'User Group': 4,
    'Walk Ins': 8,
  },
  {
    period: 'W4 Mar',
    Academic: 10,
    'Community Affairs': 7,
    Executive: 15,
    Government: 6,
    'Human Resources': 8,
    'MS Customer': 14,
    'MS Internal': 17,
    'MS Partner': 4,
    'Non-MS Business': 5,
    'Non-Profit': 3,
    Training: 7,
    'User Group': 5,
    'Walk Ins': 8,
  },
];

function getTier(utilization: number): HeatmapTier {
  if (utilization >= 85) return 'critical';
  if (utilization >= 70) return 'high';
  if (utilization >= 50) return 'medium';
  if (utilization >= 30) return 'moderate';
  return 'low';
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 14 }, (_, i) => i + 7);

const heatmapRaw: number[][] = [
  [24, 46, 62, 78, 85, 69, 59, 82, 76, 64, 42, 20, 11, 5],
  [26, 48, 66, 87, 91, 73, 62, 88, 82, 69, 44, 22, 12, 6],
  [28, 54, 71, 93, 96, 79, 66, 92, 88, 73, 49, 25, 13, 7],
  [27, 52, 69, 90, 93, 76, 64, 91, 85, 71, 47, 24, 12, 6],
  [20, 39, 54, 69, 74, 62, 52, 66, 59, 49, 34, 15, 8, 4],
  [13, 22, 33, 42, 46, 37, 29, 35, 27, 19, 12, 7, 4, 2],
  [8, 15, 21, 27, 29, 23, 17, 21, 16, 11, 7, 4, 2, 1],
];

export const MOCK_HEATMAP_DATA: HeatmapCell[] = DAYS.flatMap((day, di) =>
  HOURS.map((hour, hi) => ({
    day,
    hour,
    utilization: heatmapRaw[di]![hi]!,
    tier: getTier(heatmapRaw[di]![hi]!),
  }))
);

export const ROOM_TYPES: RoomTypeConfig[] = [
  { name: 'Conference', color: tokens.colorPaletteBlueForeground2 },
  { name: 'Lab', color: tokens.colorPalettePurpleForeground2 },
  { name: 'Boardroom', color: tokens.colorPaletteRoyalBlueForeground2 },
  { name: 'Training', color: tokens.colorPaletteGreenForeground2 },
  { name: 'Collab', color: tokens.colorPaletteBrownForeground2 },
  { name: 'Event', color: tokens.colorPaletteCranberryForeground2 },
  { name: 'Focus', color: tokens.colorPaletteMagentaForeground2 },
  { name: 'Flex', color: tokens.colorPaletteForestForeground2 },
];

export const MOCK_ROOM_DETAILS: RoomDetail[] = [
  {
    id: 'r01',
    name: 'Conf Rm A – Floor 3',
    roomType: 'Conference',
    buildingId: 'b1',
    utilization: 91,
    bookings: 312,
    avgDuration: 87,
  },
  {
    id: 'r02',
    name: 'Innovation Lab',
    roomType: 'Lab',
    buildingId: 'b1',
    utilization: 88,
    bookings: 148,
    avgDuration: 142,
  },
  {
    id: 'r03',
    name: 'Exec Boardroom',
    roomType: 'Boardroom',
    buildingId: 'b1',
    utilization: 85,
    bookings: 198,
    avgDuration: 112,
  },
  {
    id: 'r04',
    name: 'Training Rm 1',
    roomType: 'Training',
    buildingId: 'b1',
    utilization: 83,
    bookings: 176,
    avgDuration: 180,
  },
  {
    id: 'r05',
    name: 'Conf Rm B – Floor 3',
    roomType: 'Conference',
    buildingId: 'b1',
    utilization: 79,
    bookings: 289,
    avgDuration: 74,
  },
  {
    id: 'r06',
    name: 'Open Collab Pod 2',
    roomType: 'Collab',
    buildingId: 'b1',
    utilization: 76,
    bookings: 401,
    avgDuration: 45,
  },
  {
    id: 'r07',
    name: 'Focus Room 5',
    roomType: 'Focus',
    buildingId: 'b2',
    utilization: 72,
    bookings: 520,
    avgDuration: 38,
  },
  {
    id: 'r08',
    name: 'Conf Rm C – Floor 4',
    roomType: 'Conference',
    buildingId: 'b2',
    utilization: 68,
    bookings: 245,
    avgDuration: 68,
  },
  {
    id: 'r09',
    name: 'Workshop Suite B',
    roomType: 'Training',
    buildingId: 'b2',
    utilization: 65,
    bookings: 132,
    avgDuration: 165,
  },
  {
    id: 'r10',
    name: 'Open Collab Pod 1',
    roomType: 'Collab',
    buildingId: 'b2',
    utilization: 62,
    bookings: 355,
    avgDuration: 42,
  },
  {
    id: 'r11',
    name: 'Conf Rm D – Floor 5',
    roomType: 'Conference',
    buildingId: 'b2',
    utilization: 55,
    bookings: 198,
    avgDuration: 60,
  },
  {
    id: 'r12',
    name: 'Town Hall Space',
    roomType: 'Event',
    buildingId: 'b1',
    utilization: 48,
    bookings: 62,
    avgDuration: 220,
  },
  {
    id: 'r13',
    name: 'Focus Room 2',
    roomType: 'Focus',
    buildingId: 'b2',
    utilization: 38,
    bookings: 280,
    avgDuration: 35,
  },
  {
    id: 'r14',
    name: 'Conf Rm E – Floor 6',
    roomType: 'Conference',
    buildingId: 'b1',
    utilization: 38,
    bookings: 142,
    avgDuration: 55,
  },
  {
    id: 'r15',
    name: 'Legacy Suite 7A',
    roomType: 'Conference',
    buildingId: 'b2',
    utilization: 29,
    bookings: 98,
    avgDuration: 72,
  },
  {
    id: 'r16',
    name: 'Storage/Flex 2',
    roomType: 'Flex',
    buildingId: 'b2',
    utilization: 22,
    bookings: 44,
    avgDuration: 30,
  },
  {
    id: 'r17',
    name: 'Research Lab B',
    roomType: 'Lab',
    buildingId: 'b1',
    utilization: 35,
    bookings: 88,
    avgDuration: 155,
  },
  {
    id: 'r18',
    name: 'Flex Space 1',
    roomType: 'Flex',
    buildingId: 'b1',
    utilization: 18,
    bookings: 32,
    avgDuration: 25,
  },
  {
    id: 'r19',
    name: 'Boardroom West',
    roomType: 'Boardroom',
    buildingId: 'b2',
    utilization: 42,
    bookings: 110,
    avgDuration: 95,
  },
  {
    id: 'r20',
    name: 'Event Hall East',
    roomType: 'Event',
    buildingId: 'b2',
    utilization: 31,
    bookings: 28,
    avgDuration: 240,
  },
];

const ROOM_HEALTH_KPIS: KpiMetric[] = [
  {
    id: 'rooms-underutilized',
    labelKey: 'kpi.roomsUnderutilized',
    value: '7',
    trendDirection: 'negative',
    trendLabel: '< 40% utilization',
  },
  {
    id: 'rooms-healthy',
    labelKey: 'kpi.roomsHealthy',
    value: '9',
    trendDirection: 'positive',
    trendLabel: '40–80% utilization',
  },
  {
    id: 'rooms-constrained',
    labelKey: 'kpi.roomsConstrained',
    value: '4',
    trendDirection: 'negative',
    trendLabel: '≥ 80% utilization',
  },
];

export const MOCK_SCHEDULE_EVENTS: ScheduleEvent[] = [
  {
    id: 'ev01',
    title: 'Morning Standup',
    startTime: '2026-06-09T08:00:00',
    endTime: '2026-06-09T08:30:00',
    roomName: 'Conf Rm A – Floor 3',
    roomType: 'Conference',
    department: 'Engineering',
    audienceType: 'MS Internal',
    guestCount: 12,
    sponsorName: 'Sarah Chen',
  },
  {
    id: 'ev02',
    title: 'Executive Strategy Review',
    startTime: '2026-06-09T09:00:00',
    endTime: '2026-06-09T10:30:00',
    roomName: 'Exec Boardroom',
    roomType: 'Boardroom',
    department: 'Leadership',
    audienceType: 'MS Internal',
    guestCount: 8,
    sponsorName: 'James Liu',
  },
  {
    id: 'ev03',
    title: 'Customer Demo: Azure AI',
    startTime: '2026-06-09T10:00:00',
    endTime: '2026-06-09T11:30:00',
    roomName: 'Innovation Lab',
    roomType: 'Lab',
    department: 'Sales',
    audienceType: 'MS Customer',
    guestCount: 25,
    sponsorName: 'Priya Sharma',
  },
  {
    id: 'ev04',
    title: 'New Hire Orientation',
    startTime: '2026-06-09T09:00:00',
    endTime: '2026-06-09T12:00:00',
    roomName: 'Training Rm 1',
    roomType: 'Training',
    department: 'Human Resources',
    audienceType: 'MS Internal',
    guestCount: 45,
  },
  {
    id: 'ev05',
    title: 'All-Hands Town Hall',
    startTime: '2026-06-09T11:00:00',
    endTime: '2026-06-09T12:00:00',
    roomName: 'Town Hall Space',
    roomType: 'Event',
    department: 'Leadership',
    audienceType: 'MS Internal',
    guestCount: 180,
    sponsorName: 'Maria Torres',
  },
  {
    id: 'ev06',
    title: 'Design Sprint: UX Workshop',
    startTime: '2026-06-09T13:00:00',
    endTime: '2026-06-09T15:00:00',
    roomName: 'Open Collab Pod 2',
    roomType: 'Collab',
    department: 'Design',
    audienceType: 'MS Internal',
    guestCount: 15,
  },
  {
    id: 'ev07',
    title: 'Partner Integration Review',
    startTime: '2026-06-09T13:30:00',
    endTime: '2026-06-09T14:30:00',
    roomName: 'Conf Rm B – Floor 3',
    roomType: 'Conference',
    department: 'Partnerships',
    audienceType: 'MS Partner',
    guestCount: 10,
    sponsorName: 'David Kim',
  },
  {
    id: 'ev08',
    title: 'Security Compliance Training',
    startTime: '2026-06-09T14:00:00',
    endTime: '2026-06-09T16:00:00',
    roomName: 'Training Rm 1',
    roomType: 'Training',
    department: 'Security',
    audienceType: 'MS Internal',
    guestCount: 35,
  },
  {
    id: 'ev09',
    title: 'Focus Time: Q3 Planning',
    startTime: '2026-06-09T14:00:00',
    endTime: '2026-06-09T15:00:00',
    roomName: 'Focus Room 5',
    roomType: 'Focus',
    department: 'Product',
    audienceType: 'MS Internal',
    guestCount: 1,
  },
  {
    id: 'ev10',
    title: 'Community Outreach Briefing',
    startTime: '2026-06-09T15:00:00',
    endTime: '2026-06-09T16:00:00',
    roomName: 'Conf Rm C – Floor 4',
    roomType: 'Conference',
    department: 'Community Affairs',
    audienceType: 'Non-Profit',
    guestCount: 20,
    sponsorName: 'Angela Wright',
  },
  {
    id: 'ev11',
    title: 'Government Compliance Update',
    startTime: '2026-06-09T15:30:00',
    endTime: '2026-06-09T16:30:00',
    roomName: 'Boardroom West',
    roomType: 'Boardroom',
    department: 'Legal',
    audienceType: 'Government',
    guestCount: 6,
  },
  {
    id: 'ev12',
    title: 'Hackathon Kickoff',
    startTime: '2026-06-09T16:00:00',
    endTime: '2026-06-09T18:00:00',
    roomName: 'Town Hall Space',
    roomType: 'Event',
    department: 'Engineering',
    audienceType: 'MS Internal',
    guestCount: 120,
    sponsorName: 'Raj Patel',
  },
  {
    id: 'ev13',
    title: 'Vendor Onboarding',
    startTime: '2026-06-09T16:30:00',
    endTime: '2026-06-09T17:30:00',
    roomName: 'Conf Rm D – Floor 5',
    roomType: 'Conference',
    department: 'Procurement',
    audienceType: 'Non-MS Business',
    guestCount: 8,
  },
  {
    id: 'ev14',
    title: 'Evening Networking Mixer',
    startTime: '2026-06-09T17:00:00',
    endTime: '2026-06-09T19:00:00',
    roomName: 'Event Hall East',
    roomType: 'Event',
    department: 'Community Affairs',
    audienceType: 'MS Customer',
    guestCount: 60,
    sponsorName: 'Tom Wilson',
  },
  {
    id: 'ev15',
    title: 'Sprint Retrospective',
    startTime: '2026-06-10T09:00:00',
    endTime: '2026-06-10T10:00:00',
    roomName: 'Conf Rm A – Floor 3',
    roomType: 'Conference',
    department: 'Engineering',
    audienceType: 'MS Internal',
    guestCount: 10,
  },
  {
    id: 'ev16',
    title: 'Product Review Board',
    startTime: '2026-06-10T10:30:00',
    endTime: '2026-06-10T12:00:00',
    roomName: 'Exec Boardroom',
    roomType: 'Boardroom',
    department: 'Product',
    audienceType: 'MS Internal',
    guestCount: 14,
    sponsorName: 'James Liu',
  },
  {
    id: 'ev17',
    title: 'Customer Success Workshop',
    startTime: '2026-06-10T13:00:00',
    endTime: '2026-06-10T15:00:00',
    roomName: 'Training Rm 1',
    roomType: 'Training',
    department: 'Sales',
    audienceType: 'MS Customer',
    guestCount: 50,
    sponsorName: 'Priya Sharma',
  },
  {
    id: 'ev18',
    title: 'Team Building Activity',
    startTime: '2026-06-10T15:00:00',
    endTime: '2026-06-10T17:00:00',
    roomName: 'Open Collab Pod 2',
    roomType: 'Collab',
    department: 'Human Resources',
    audienceType: 'MS Internal',
    guestCount: 22,
  },
];

export const BUSINESS_GROUPS: BusinessGroupConsumption[] = [
  {
    id: 'bg-01',
    name: 'Cloud + AI',
    color: tokens.colorPaletteBlueForeground2,
    bookedHours: 3840,
    bookings: 612,
    utilization: 84,
    purposeBreakdown: {
      Academic: 320,
      'Community Affairs': 160,
      Executive: 480,
      Government: 120,
      'Human Resources': 200,
      'MS Customer': 640,
      'MS Internal': 800,
      'MS Partner': 160,
      'Non-MS Business': 240,
      'Non-Profit': 80,
      Training: 320,
      'User Group': 160,
      'Walk Ins': 160,
    },
    spaceTypeBreakdown: {
      Conference: 1200,
      Training: 800,
      Collab: 520,
      Focus: 360,
      Boardroom: 320,
      Lab: 280,
      Event: 240,
      Flex: 120,
    },
  },
  {
    id: 'bg-02',
    name: 'CoreAI',
    color: tokens.colorPaletteTealForeground2,
    bookedHours: 3200,
    bookings: 510,
    utilization: 79,
    purposeBreakdown: {
      Academic: 280,
      'Community Affairs': 120,
      Executive: 400,
      Government: 100,
      'Human Resources': 180,
      'MS Customer': 520,
      'MS Internal': 720,
      'MS Partner': 140,
      'Non-MS Business': 200,
      'Non-Profit': 60,
      Training: 240,
      'User Group': 120,
      'Walk Ins': 120,
    },
    spaceTypeBreakdown: {
      Conference: 1000,
      Training: 680,
      Collab: 440,
      Focus: 300,
      Boardroom: 260,
      Lab: 240,
      Event: 180,
      Flex: 100,
    },
  },
  {
    id: 'bg-03',
    name: 'MS AI',
    color: tokens.colorPalettePurpleForeground2,
    bookedHours: 2980,
    bookings: 475,
    utilization: 77,
    purposeBreakdown: {
      Academic: 260,
      'Community Affairs': 110,
      Executive: 380,
      Government: 90,
      'Human Resources': 160,
      'MS Customer': 500,
      'MS Internal': 680,
      'MS Partner': 120,
      'Non-MS Business': 180,
      'Non-Profit': 50,
      Training: 220,
      'User Group': 110,
      'Walk Ins': 120,
    },
    spaceTypeBreakdown: {
      Conference: 920,
      Training: 640,
      Collab: 400,
      Focus: 280,
      Boardroom: 240,
      Lab: 220,
      Event: 170,
      Flex: 110,
    },
  },
  {
    id: 'bg-04',
    name: 'Experiences & Devices',
    color: tokens.colorPaletteGreenForeground2,
    bookedHours: 2750,
    bookings: 440,
    utilization: 75,
    purposeBreakdown: {
      Academic: 240,
      'Community Affairs': 100,
      Executive: 350,
      Government: 80,
      'Human Resources': 150,
      'MS Customer': 460,
      'MS Internal': 620,
      'MS Partner': 110,
      'Non-MS Business': 170,
      'Non-Profit': 50,
      Training: 200,
      'User Group': 100,
      'Walk Ins': 120,
    },
    spaceTypeBreakdown: {
      Conference: 860,
      Training: 580,
      Collab: 380,
      Focus: 260,
      Boardroom: 220,
      Lab: 200,
      Event: 150,
      Flex: 100,
    },
  },
  {
    id: 'bg-05',
    name: 'Copilot',
    color: tokens.colorPaletteRedForeground2,
    bookedHours: 2500,
    bookings: 400,
    utilization: 72,
    purposeBreakdown: {
      Academic: 220,
      'Community Affairs': 90,
      Executive: 320,
      Government: 80,
      'Human Resources': 140,
      'MS Customer': 420,
      'MS Internal': 560,
      'MS Partner': 100,
      'Non-MS Business': 150,
      'Non-Profit': 40,
      Training: 180,
      'User Group': 100,
      'Walk Ins': 100,
    },
    spaceTypeBreakdown: {
      Conference: 780,
      Training: 520,
      Collab: 340,
      Focus: 240,
      Boardroom: 200,
      Lab: 180,
      Event: 140,
      Flex: 100,
    },
  },
  {
    id: 'bg-06',
    name: 'Chief Finance Officer',
    color: tokens.colorPaletteRoyalBlueForeground2,
    bookedHours: 2300,
    bookings: 370,
    utilization: 70,
    purposeBreakdown: {
      Academic: 200,
      'Community Affairs': 80,
      Executive: 300,
      Government: 70,
      'Human Resources': 130,
      'MS Customer': 380,
      'MS Internal': 520,
      'MS Partner': 90,
      'Non-MS Business': 140,
      'Non-Profit': 40,
      Training: 170,
      'User Group': 90,
      'Walk Ins': 90,
    },
    spaceTypeBreakdown: {
      Conference: 720,
      Training: 480,
      Collab: 320,
      Focus: 220,
      Boardroom: 180,
      Lab: 160,
      Event: 120,
      Flex: 100,
    },
  },
  {
    id: 'bg-07',
    name: 'Marketing',
    color: tokens.colorPaletteCranberryForeground2,
    bookedHours: 2100,
    bookings: 340,
    utilization: 68,
    purposeBreakdown: {
      Academic: 180,
      'Community Affairs': 80,
      Executive: 270,
      Government: 60,
      'Human Resources': 120,
      'MS Customer': 350,
      'MS Internal': 480,
      'MS Partner': 80,
      'Non-MS Business': 120,
      'Non-Profit': 30,
      Training: 160,
      'User Group': 80,
      'Walk Ins': 90,
    },
    spaceTypeBreakdown: {
      Conference: 660,
      Training: 440,
      Collab: 280,
      Focus: 200,
      Boardroom: 160,
      Lab: 140,
      Event: 120,
      Flex: 100,
    },
  },
  {
    id: 'bg-08',
    name: 'CELA',
    color: tokens.colorPaletteForestForeground2,
    bookedHours: 1950,
    bookings: 315,
    utilization: 66,
    purposeBreakdown: {
      Academic: 170,
      'Community Affairs': 70,
      Executive: 250,
      Government: 60,
      'Human Resources': 110,
      'MS Customer': 320,
      'MS Internal': 440,
      'MS Partner': 80,
      'Non-MS Business': 110,
      'Non-Profit': 30,
      Training: 150,
      'User Group': 80,
      'Walk Ins': 80,
    },
    spaceTypeBreakdown: {
      Conference: 600,
      Training: 420,
      Collab: 260,
      Focus: 180,
      Boardroom: 160,
      Lab: 120,
      Event: 110,
      Flex: 100,
    },
  },
  {
    id: 'bg-09',
    name: 'LinkedIn + Offices',
    color: tokens.colorPalettePlumForeground2,
    bookedHours: 1800,
    bookings: 290,
    utilization: 63,
    purposeBreakdown: {
      Academic: 160,
      'Community Affairs': 60,
      Executive: 230,
      Government: 50,
      'Human Resources': 100,
      'MS Customer': 300,
      'MS Internal': 400,
      'MS Partner': 70,
      'Non-MS Business': 110,
      'Non-Profit': 30,
      Training: 140,
      'User Group': 70,
      'Walk Ins': 80,
    },
    spaceTypeBreakdown: {
      Conference: 560,
      Training: 380,
      Collab: 240,
      Focus: 160,
      Boardroom: 140,
      Lab: 120,
      Event: 100,
      Flex: 100,
    },
  },
  {
    id: 'bg-10',
    name: 'Xbox',
    color: tokens.colorPaletteBrownForeground2,
    bookedHours: 1650,
    bookings: 265,
    utilization: 60,
    purposeBreakdown: {
      Academic: 140,
      'Community Affairs': 60,
      Executive: 210,
      Government: 50,
      'Human Resources': 90,
      'MS Customer': 280,
      'MS Internal': 360,
      'MS Partner': 60,
      'Non-MS Business': 100,
      'Non-Profit': 30,
      Training: 130,
      'User Group': 70,
      'Walk Ins': 70,
    },
    spaceTypeBreakdown: {
      Conference: 520,
      Training: 340,
      Collab: 220,
      Focus: 150,
      Boardroom: 130,
      Lab: 100,
      Event: 100,
      Flex: 90,
    },
  },
  {
    id: 'bg-13',
    name: 'Microsoft Security',
    color: tokens.colorPaletteSeafoamForeground2,
    bookedHours: 1200,
    bookings: 195,
    utilization: 52,
    purposeBreakdown: {
      Academic: 100,
      'Community Affairs': 40,
      Executive: 160,
      Government: 30,
      'Human Resources': 70,
      'MS Customer': 200,
      'MS Internal': 270,
      'MS Partner': 50,
      'Non-MS Business': 70,
      'Non-Profit': 20,
      Training: 100,
      'User Group': 50,
      'Walk Ins': 40,
    },
    spaceTypeBreakdown: {
      Conference: 380,
      Training: 250,
      Collab: 160,
      Focus: 110,
      Boardroom: 100,
      Lab: 80,
      Event: 60,
      Flex: 60,
    },
  },
  {
    id: 'bg-16',
    name: 'Operations',
    color: tokens.colorPalettePeachForeground2,
    bookedHours: 750,
    bookings: 120,
    utilization: 40,
    purposeBreakdown: {
      Academic: 60,
      'Community Affairs': 30,
      Executive: 100,
      Government: 20,
      'Human Resources': 40,
      'MS Customer': 120,
      'MS Internal': 170,
      'MS Partner': 30,
      'Non-MS Business': 40,
      'Non-Profit': 10,
      Training: 60,
      'User Group': 30,
      'Walk Ins': 40,
    },
    spaceTypeBreakdown: {
      Conference: 240,
      Training: 160,
      Collab: 100,
      Focus: 70,
      Boardroom: 60,
      Lab: 40,
      Event: 40,
      Flex: 40,
    },
  },
  {
    id: 'bg-17',
    name: 'MS Commercial',
    color: tokens.colorPaletteMarigoldForeground2,
    bookedHours: 550,
    bookings: 88,
    utilization: 35,
    purposeBreakdown: {
      Academic: 40,
      'Community Affairs': 30,
      Executive: 70,
      Government: 10,
      'Human Resources': 30,
      'MS Customer': 90,
      'MS Internal': 120,
      'MS Partner': 20,
      'Non-MS Business': 30,
      'Non-Profit': 20,
      Training: 50,
      'User Group': 20,
      'Walk Ins': 20,
    },
    spaceTypeBreakdown: {
      Conference: 170,
      Training: 120,
      Collab: 80,
      Focus: 50,
      Boardroom: 40,
      Lab: 30,
      Event: 30,
      Flex: 30,
    },
  },
];

const BUSINESS_GROUP_KPIS: KpiMetric[] = [
  {
    id: 'bg-total-hours',
    labelKey: 'kpi.bgTotalHours',
    value: '22,290 h',
    unit: 'h',
    trendDirection: 'positive',
    trendLabel: 'All groups combined',
  },
  {
    id: 'bg-top-consumer',
    labelKey: 'kpi.bgTopConsumer',
    value: 'Cloud + AI',
    trendDirection: 'positive',
    trendLabel: '17.2% of total',
  },
  {
    id: 'bg-total-bookings',
    labelKey: 'kpi.bgTotalBookings',
    value: '4,092',
    trendDirection: 'positive',
    trendLabel: 'Across all groups',
  },
  {
    id: 'bg-avg-per-group',
    labelKey: 'kpi.bgAvgPerGroup',
    value: '1,311 h',
    unit: 'h',
    trendDirection: 'neutral',
    trendLabel: 'Per business group',
  },
];

const SURVEY_KPIS: KpiMetric[] = [
  {
    id: 'survey-overall-csat',
    labelKey: 'kpi.overallCSAT',
    value: '4.2',
    trendDirection: 'positive',
    trendValue: '↑ 0.3',
    subtitle: '/5.0',
    ratingValue: 4.2,
  },
  {
    id: 'survey-total-responses',
    labelKey: 'kpi.totalResponses',
    value: '3,847',
    trendDirection: 'positive',
    trendValue: '↑ 12%',
  },
  {
    id: 'survey-avg-response-rate',
    labelKey: 'kpi.avgResponseRate',
    value: '73%',
    trendDirection: 'positive',
    trendValue: '↑ 5%',
  },
  {
    id: 'survey-top-rated-space',
    labelKey: 'kpi.topRatedSpace',
    value: 'Innovation Lab',
    trendDirection: 'neutral',
    trendLabel: '4.8 ★',
  },
];

const SURVEY_SITE_CSAT: SurveySiteCSAT[] = [
  { siteName: 'Building 25', csatScore: 4.5, responseCount: 842, responseRate: 81 },
  { siteName: 'Building 34', csatScore: 4.2, responseCount: 623, responseRate: 74 },
  { siteName: 'Building 18', csatScore: 3.8, responseCount: 510, responseRate: 69 },
  { siteName: 'Innovation Lab', csatScore: 4.8, responseCount: 389, responseRate: 88 },
  { siteName: 'Campus Center', csatScore: 3.5, responseCount: 720, responseRate: 65 },
  { siteName: 'Building 42', csatScore: 2.9, responseCount: 763, responseRate: 58 },
];

const SURVEY_TREND: SurveyTrendPoint[] = [
  { month: 'Jan 2026', csatScore: 3.9 },
  { month: 'Feb 2026', csatScore: 4.0 },
  { month: 'Mar 2026', csatScore: 3.8 },
  { month: 'Apr 2026', csatScore: 4.1 },
  { month: 'May 2026', csatScore: 4.3 },
  { month: 'Jun 2026', csatScore: 4.2 },
];

const SURVEY_BY_PURPOSE: PurposeSatisfaction[] = [
  { purpose: 'Academic', csatScore: 4.3 },
  { purpose: 'Executive', csatScore: 4.1 },
  { purpose: 'Government', csatScore: 3.6 },
  { purpose: 'Community Affairs', csatScore: 4.5 },
  { purpose: 'Workshop', csatScore: 3.9 },
  { purpose: 'All Hands', csatScore: 3.2 },
];

const SURVEY_BY_SPACE_TYPE: SpaceTypeSatisfaction[] = [
  { spaceType: 'Conference', csatScore: 4.1 },
  { spaceType: 'Boardroom', csatScore: 4.4 },
  { spaceType: 'Training', csatScore: 3.7 },
  { spaceType: 'Lab', csatScore: 4.6 },
  { spaceType: 'Collaboration', csatScore: 3.9 },
  { spaceType: 'Focus Room', csatScore: 3.5 },
  { spaceType: 'Event Space', csatScore: 4.2 },
];

export const MOCK_DASHBOARD_CONFIG: DashboardConfig = {
  tabs: [
    {
      id: 'space-utilization',
      labelKey: 'tabs.spaceUtilization',
      subtitleKey: 'tabs.spaceUtilizationSub',
      kpiIds: ['avg-utilization', 'total-booked-hours', 'available-hours', 'peak-day'],
      widgets: [
        {
          id: 'business-purpose',
          type: 'stacked-bar',
          titleKey: 'widgets.businessPurposeTitle',
          layout: 'full',
          dataKey: 'businessPurpose',
        },
        {
          id: 'utilization-trend',
          type: 'line',
          titleKey: 'widgets.utilizationTrendTitle',
          layout: 'half',
          dataKey: 'utilizationTrend',
        },
        {
          id: 'day-hour-heatmap',
          type: 'heatmap',
          titleKey: 'widgets.heatmapTitle',
          layout: 'half',
          dataKey: 'heatmap',
        },
      ],
    },
    {
      id: 'room-space',
      labelKey: 'tabs.roomSpace',
      subtitleKey: 'tabs.roomSpaceSub',
      kpiIds: ['rooms-underutilized', 'rooms-healthy', 'rooms-constrained'],
      kpiRenderer: 'room-health' as const,
      widgets: [
        {
          id: 'room-ranking',
          type: 'horizontal-bar',
          titleKey: 'widgets.roomRankingTitle',
          layout: 'full',
          dataKey: 'roomDetails',
          variant: 'ranking',
        },
        {
          id: 'util-by-room-type',
          type: 'horizontal-bar',
          titleKey: 'widgets.utilByRoomTypeTitle',
          layout: 'half',
          dataKey: 'roomDetails',
          variant: 'by-type-pct',
        },
        {
          id: 'bookings-by-type',
          type: 'horizontal-bar',
          titleKey: 'widgets.bookingsByTypeTitle',
          layout: 'half',
          dataKey: 'roomDetails',
          variant: 'by-type-count',
        },
        {
          id: 'room-detail-table',
          type: 'data-table',
          titleKey: 'widgets.roomDetailTableTitle',
          layout: 'full',
          dataKey: 'roomDetails',
        },
      ],
    },
    {
      id: 'run-of-show',
      labelKey: 'tabs.runOfShow',
      subtitleKey: 'tabs.runOfShowSub',
      kpiIds: [],
      widgets: [
        {
          id: 'run-of-show-timeline',
          type: 'schedule-timeline',
          titleKey: 'widgets.runOfShowTimelineTitle',
          layout: 'full',
          dataKey: 'scheduleEvents',
        },
      ],
    },
    {
      id: 'business-group',
      labelKey: 'tabs.businessGroup',
      subtitleKey: 'tabs.businessGroupSub',
      kpiIds: ['bg-total-hours', 'bg-top-consumer', 'bg-total-bookings', 'bg-avg-per-group'],
      widgets: [
        {
          id: 'group-purpose',
          type: 'group-purpose-bar',
          titleKey: 'widgets.groupPurposeBarTitle',
          layout: 'half',
          dataKey: 'businessGroups',
        },
        {
          id: 'group-space',
          type: 'group-space-bar',
          titleKey: 'widgets.groupSpaceBarTitle',
          layout: 'half',
          dataKey: 'businessGroups',
        },
        {
          id: 'group-consumption',
          type: 'group-consumption-table',
          titleKey: 'widgets.groupConsumptionTableTitle',
          layout: 'full',
          dataKey: 'businessGroups',
        },
      ],
    },
    {
      id: 'satisfaction-survey',
      labelKey: 'tabs.satisfactionSurvey',
      subtitleKey: 'tabs.satisfactionSurveySub',
      kpiIds: [
        'survey-overall-csat',
        'survey-total-responses',
        'survey-avg-response-rate',
        'survey-top-rated-space',
      ],
      widgets: [
        {
          id: 'csat-site-grid',
          type: 'survey-site-grid',
          titleKey: 'widgets.csatBySiteTitle',
          layout: 'full',
          dataKey: 'surveySiteCSAT',
        },
        {
          id: 'csat-trend-line',
          type: 'survey-trend-line',
          titleKey: 'widgets.csatTrendTitle',
          layout: 'half',
          dataKey: 'surveyTrend',
        },
        {
          id: 'csat-polar',
          type: 'survey-polar',
          titleKey: 'widgets.csatPolarTitle',
          layout: 'half',
          dataKey: 'surveyBySpaceType',
        },
        {
          id: 'csat-purpose-bar',
          type: 'survey-purpose-bar',
          titleKey: 'widgets.csatByPurposeTitle',
          layout: 'half',
          dataKey: 'surveyByPurpose',
        },
        {
          id: 'csat-space-bar',
          type: 'survey-space-bar',
          titleKey: 'widgets.csatBySpaceTypeTitle',
          layout: 'half',
          dataKey: 'surveyBySpaceType',
        },
      ],
    },
  ],
  kpis: [...MOCK_KPI_METRICS, ...ROOM_HEALTH_KPIS, ...BUSINESS_GROUP_KPIS, ...SURVEY_KPIS],
  datasets: {
    businessPurpose: MOCK_BUSINESS_PURPOSE_DATA,
    utilizationTrend: MOCK_UTILIZATION_TREND,
    heatmap: MOCK_HEATMAP_DATA,
    roomDetails: MOCK_ROOM_DETAILS,
    scheduleEvents: MOCK_SCHEDULE_EVENTS,
    businessGroups: BUSINESS_GROUPS,
    surveySiteCSAT: SURVEY_SITE_CSAT,
    surveyTrend: SURVEY_TREND,
    surveyByPurpose: SURVEY_BY_PURPOSE,
    surveyBySpaceType: SURVEY_BY_SPACE_TYPE,
  },
  purposes: BUSINESS_PURPOSES,
  roomTypes: ROOM_TYPES,
  largeEventThreshold: 40,
};
